import { useEffect } from 'react';

import { AUTH_STORAGE_KEY } from './use-auth';

const LOGIN_PATH = '/login';

/**
 * Lets a tab stranded on the sign-in screen notice a sign-in that happened in
 * another one.
 *
 * The token lives in localStorage, which every tab on this origin already
 * shares — so signing in anywhere signs them all in. Nothing here notices,
 * because the check that decides runs once on mount. The person is signed in
 * and is being asked to sign in again, which is what happens whenever the
 * career builder sends someone off to re-authenticate in a second tab.
 *
 * `storage` fires only in the OTHER tabs, which is exactly the ones that need
 * to hear it.
 *
 * Deliberately scoped to the sign-in screen: reloading a tab somebody is
 * working in would throw away whatever they had typed, and there is nothing to
 * lose on a sign-in form. A reload rather than a route change, because
 * bootstrap and the providers all read the token at start-up.
 */
export const useSignInFromAnotherTab = () => {
  useEffect(() => {
    const onTokenArrived = (event: StorageEvent) => {
      if (event.key !== AUTH_STORAGE_KEY || !event.newValue) return;
      if (window.location.pathname !== LOGIN_PATH) return;

      window.location.reload();
    };

    window.addEventListener('storage', onTokenArrived);

    return () => window.removeEventListener('storage', onTokenArrived);
  }, []);
};
